import { uniq } from "lodash";

export const GROUPS = {
  WOMEN: "WOMEN",
  MEN: "MEN",
  KIDS: "KIDS",
  HOME_OR_PETS: "HOME_OR_PETS"
};

export const LABELS = {
  [GROUPS.WOMEN]: "Women",
  [GROUPS.MEN]: "Men",
  [GROUPS.KIDS]: "Kids",
  [GROUPS.HOME_OR_PETS]: "Home or Pets"
};

/**
 * @type {{ [group: string]: { [label: string]: Array<string | number>}}}
 * @description See https://docs.google.com/spreadsheets/d/1bpc-Ln5e7KqctDazjDTjr9uDez3DWivFAdNpZhqsdx4/edit#gid=1917687489 for current definition of these sizes
 */
export const SIZES_MAPPINGS = {
  [GROUPS.WOMEN]: {
    "US - APPAREL": ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "4XL", "5XL", "6XL"],
    "US - APPAREL (GROUPED)": ["XXS/XS", "XS/S", "S/M", "M/L", "L/XL"],
    "US - APPAREL (NUMERIC - ALT)": ["0", 1, 2, 3, 4, 5],
    "US - APPAREL (NUMERIC)": ["00", "0", 2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
    "US - PETITE APPAREL (NUMERIC)": ["00P", "0P", "2P", "4P", "6P", "8P", "10P", "12P", "14P", "16P"],
    "US - FOOTWEAR (WOMEN'S)": [5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13],
    "US - BOTTOMS": [23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34],
    "UK - APPAREL": [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24],
    "EU - APPAREL": [30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50, 52],
    "EU - FOOTWEAR": [
      35,
      35.5,
      36,
      36.5,
      37,
      37.5,
      38,
      38.5,
      39,
      39.5,
      40,
      40.5,
      41,
      41.5,
      42,
      42.5,
      43,
      43.5,
      44,
      44.5,
      45
    ],
    "WOMENS - ONE SIZE": ["O/S"],
    "WOMENS - BELTS": ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
    "WOMENS - BELTS - ALT": [30, 32, 33, 34, 35, 36, 38, 40, 42, 44, 46]
  },
  [GROUPS.MEN]: {
    "US - BOTTOMS": [28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 44, 46, 48],
    "MENS - BELTS": [30, 32, 33, 34, 35, 36, 38, 40, 42, 44, 46],
    "EU - TOPS": [40, 41, 42, 43, 44, 45, 46, 47, 48, 50, 52],
    "EU - FOOTWEAR": [41, 41.5, 42, 42.5, 43, 43.5, 44, 44.5, 45, 46, 47, 48],
    "US - FOOTWEAR": [6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 14, 15],
    "MENS - ONE SIZE": ["O/S"],
    "US - APPAREL": ["XS", "S", "M", "L", "XL", "XXL", "XXXL"]
  },
  [GROUPS.KIDS]: {
    "US - APPAREL": ["XS", "S", "M", "L", "XL"],
    "INFANT - APPAREL": [
      "0-3 Months",
      "3-6 Months",
      "6-9 Months",
      "9-12 Months",
      "12-18 Months",
      "18-24 Months",
      "Newborn"
    ],
    "TODDLER - APPAREL": [
      "2T",
      "3T",
      "4T",
      "5T",
      "2",
      "3",
      "4",
      "5",
      "6",
      "6X",
      "7",
      "7X",
      "8",
      "10",
      "12",
      "14",
      "16",
      "18",
      "20"
    ],
    "EU - FOOTWEAR": [
      15,
      16,
      17,
      18,
      19,
      20,
      21,
      22,
      23,
      24,
      25,
      26,
      27,
      28,
      29,
      30,
      31,
      32,
      33,
      34,
      35,
      36,
      37,
      38,
      39
    ],
    "KIDS - ONE SIZE": ["O/S"]
  },
  [GROUPS.HOME_OR_PETS]: { "ONE SIZE FITS ALL": ["O/S"] }
};

export const ALL_VALID_SIZES = uniq(
  Object.values(SIZES_MAPPINGS).flatMap(value => Object.values(value).flat())
).sort();

/**
 * @param {Array<string | number>} list
 * @return {Array<{ size: string | number, quantity: number}>}
 */
export const buildSizeMapOptions = (list = []) => list.map(v => { return { size: v, quantity: 0 } });

export const SIZES_LIST = [
  "0-3 Months", "3-6 Months", "6-9 Months", "9-12 Months", "12-18 Months", "18-24 Months", "Newborn",
  "OS", "O/S",
  "XXXS", "Xxxxsmall", "XXX-SMALL", "XXX-Small", "XXXS/XXS",
  "XXS", "Xxsmall", "XX-SMALL", "XX-Small", "XXS/XS",
  "XS", "Xsmall", "X-SMALL", "X-Small", "XS/S",
  "S", "Small", "SMALL", "S/M",
  "M", "Medium", "MEDIUM", "M/L",
  "L", "Large", "LARGE", "L/XL",
  "XL", "Xlarge", "X-LARGE", "X-Large",
  "XL/XXL", "XXL", "Xxlarge", "XX-LARGE", "XX-Large",
  "XXL/XXXL", "XXXL", "Xxxlarge", "XXX-LARGE", "XXX-Large",
  "4XL", "5XL", "6XL",
  "00", "0", "1X", "2T", "3T", "4T", "5T", "2", "2X", "3", "3X", "4", "4X", "5", "5X", "6", "6X", "7", "7X",
  // "8" - "30"
  // TODO: Issue MRP-2769
  // eslint-disable-next-line line-comment-position
  ...Array.from({ length: 23 }, (_, i) => `${i + 8}`),
  "00P", "0P", "2P", "4P", "6P", "8P", "10P", "12P", "14P", "16P"
];
